import React, { useEffect, useState } from "react";
import {
  Button,
  Row,
  Layout,
  Input,
  Form,
  Upload,
  message,
  Typography,
  Alert,
  Popconfirm,
  Breadcrumb,
} from "antd";
import { DeleteOutlined, InboxOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../utils/Auth/AuthWrapper";
import { get_news, news_create, news_update } from "../../services/news";
import { upload_file } from "../../services/uploads";
import EditorContentPage from "../../components/Editor";
import { useTranslation } from "react-i18next";

const { Content } = Layout;
const { Dragger } = Upload;
const { Title } = Typography;

const CreateNews: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const auth = useAuth();
  const [editorContentNews, setEditorContentNews] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [imageUrluse, setImageUrluse] = useState<File | null>(null);

  // view image
  const getBase64 = (file: File, callback: (imageUrl: string) => void) => {
    const reader = new FileReader();
    reader.onload = () => callback(reader.result as string);
    reader.readAsDataURL(file);
  };

  // create news
  const handleCreateNews = async (value: any) => {
    setSubmitting(true);
    try {
      let uploadedImgUrl = null;
      if (imageUrluse) {
        try {
          const uploadResponse = await upload_file({
            task_id: "news_image",
            type: "img_news",
            name: imageUrluse.name,
            file: imageUrluse,
          });
          if (uploadResponse.status === "success") {
            uploadedImgUrl = uploadResponse.link;
          } else {
            throw new Error("Failed to upload image.");
          }
        } catch (uploadError) {
          console.error("Error uploading image:", uploadError);
          message.error("Failed to upload image.");
          return;
        }
      }
      const res = await news_create({
        user_id: auth.user.user_id,
        title: form.getFieldValue("title"),
        date: form.getFieldValue("date"),
        details: [{ text: editorContentNews || form.getFieldValue("details") }], // ✅ ป้องกัน details หาย
        img:
          uploadedImgUrl ||
          "https://thumb.ac-illust.com/b1/b170870007dfa419295d949814474ab2_t.jpeg",
      });
      if (res.status == "success") {
        message.success("Submit Success!");
        navigate("/news");
      }
    } catch (e) {
      console.error(e);
    }
    setSubmitting(false);
  };

  // check create or edit
  useEffect(() => {
    if (params.action === "edit" && params.id) {
      getNewsDetail(); // ดึงข้อมูลเมื่ออยู่ในโหมดแก้ไขและมี news_id
    }
  }, [params.action, params.id]);

  // get by id
  const getNewsDetail = async () => {
    try {
      const res = await get_news({ news_id: params.id });
      if (res.result) {
        let rawDetails = JSON.parse(res.result[0].details);
        let extractedHtml = Array.isArray(rawDetails)
          ? rawDetails
              .flatMap((item) => item.text)
              .flatMap((subItem) => subItem.text || subItem)
              .join("")
          : "";

        form.setFieldsValue({
          title: res.result[0].title,
          details: extractedHtml,
          img: res.result[0].img,
        });

        setEditorContentNews(extractedHtml); // ✅ เซ็ตค่า details ให้ editor
        setImageUrl(res.result[0].img);

        // ตรวจสอบว่าเป็นลิงก์ภายนอกหรือไม่
        const isExternalLink = /^(https:\/\/|http:\/\/|www\.)/i.test(
          res.result[0].img,
        );

        if (res.result[0].img && !isExternalLink) {
          // ถ้าไม่ใช่ลิงก์ภายนอกให้แสดงภาพ
          const fullImagePath = `${process.env.REACT_APP_API}${res.result[0].img}`;
          setImageUrl(fullImagePath);
        } else {
          // ถ้าเป็นลิงก์ภายนอกจะไม่แสดงภาพ
          setImageUrl(null);
        }
      } else {
        console.error("No details found in the response");
      }
    } catch (error) {
      console.error("Failed to fetch news details", error);
    }
  };

  // edit
  const handleUpdateNews = async (value: any) => {
    let uploadedImgUrl = form.getFieldValue("img");
    if (imageUrluse) {
      try {
        const uploadResponse = await upload_file({
          task_id: "news_image",
          type: "img_news",
          name: imageUrluse.name,
          file: imageUrluse,
        });
        if (uploadResponse.status === "success") {
          uploadedImgUrl = uploadResponse.link;
        } else {
          throw new Error("Failed to upload image.");
        }
      } catch (uploadError) {
        console.error("Error uploading image:", uploadError);
        message.error("Failed to upload image.");
        return;
      }
    }

    try {
      // const formattedDetails = [{ text: editorContentNews }];
      const res = await news_update({
        news_id: params.id,
        user_id: auth.user.user_id,
        title: form.getFieldValue("title"),
        details: [{ text: editorContentNews || form.getFieldValue("details") }],
        img: uploadedImgUrl,
      });

      if (res.status == "success") {
        message.success("Update Success!");
        // getNewsDetail();
        navigate("/news");
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="add-task-layout">
      <Content className="create-news-layout">
        <div>
          <Row justify="space-between" align="middle">
            <Breadcrumb
              separator=">"
              items={[
                {
                  title: (
                    <span style={{ fontWeight: "bold" }}>
                      {params.action === "create"
                        ? t("Add News")
                        : t("Edit News")}
                    </span>
                  ),
                },
              ]}
            />
          </Row>
          <hr />
          {/* <Divider /> */}
        </div>
      </Content>

      {/* form */}
      <Content style={{ padding: "0px 30px 20px 30px" }}>
        <Form
          form={form}
          name="image-upload-form"
          onFinish={
            params.action == "create" ? handleCreateNews : handleUpdateNews
          }
          layout="vertical"
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "24px 0px 24px 0px",
          }}
        >
          <Form.Item>
            <Dragger
              name="file"
              accept=".png, .jpeg, .jpg"
              multiple={false}
              showUploadList={false}
              onChange={async (info: any) => {
                const file = info.file.originFileObj;
                if (file) {
                  setImageUrluse(file);
                  getBase64(file, (imageUrl: string) => setImageUrl(imageUrl));
                } else {
                  console.error("File is undefined");
                }
              }}
            >
              {imageUrl ? (
                <img src={imageUrl} alt="avatar" className="show-image" />
              ) : (
                <div>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {t("Drag your image here, or browse")}
                  </p>
                  <p className="ant-upload-hint">
                    {t("Support Only PNG and JPEG files are allowed")}
                  </p>
                </div>
              )}
            </Dragger>

            <Alert
              style={{ marginTop: 20 }}
              showIcon
              message={`${t("The image size should be")} 1024 * 537`}
              type="info"
              closable
            />

            {imageUrl && (
              <Content
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Button
                  type="default"
                  danger
                  icon={<DeleteOutlined />}
                  style={{
                    marginTop: "10px",
                    justifyContent: "center",
                  }}
                  onClick={() => {
                    form.setFieldsValue({ img: null });
                    setImageUrl(null);
                  }}
                >
                  {t("Delete")}
                </Button>
              </Content>
            )}
          </Form.Item>

          <Form.Item
            name="title"
            label={t("Title News")}
            rules={[
              { required: true, message: "Please input the title!" },
              {
                validator: (_, value) =>
                  value.trim() !== ""
                    ? Promise.resolve()
                    : Promise.reject(new Error("กรุณากรอกข้อมูล")),
              },
            ]}
          >
            <Input placeholder="Title" />
          </Form.Item>

          <Form.Item
            label={t("Details News")}
            rules={[{ required: false, message: "Please input the title!" }]}
          >
            <EditorContentPage
              value={editorContentNews}
              onChange={setEditorContentNews}
            />
          </Form.Item>

          {/* summit */}
          <Form.Item>
            <Popconfirm
              title={
                params.action === "create"
                  ? t("Alert confirm create title")
                  : t("Alert confirm update title")
              }
              description={
                params.action === "create"
                  ? t("Alert confirm create detail")
                  : t("Alert confirm update detail")
              }
              onConfirm={() => form.submit()}
              okText={t("yes")}
              cancelText={t("no")}
            >
              <Button
                type="primary"
                htmlType="button"
                loading={submitting}
                style={{ float: "right", width: "100px" }}
              >
                {params.action === "create" ? t("create") : t("update")}
              </Button>
            </Popconfirm>

            <Popconfirm
              title={t("Alert title")}
              description={t(
                "Are you sure you want to cancel and return to the news page",
              )}
              onConfirm={() => navigate("/news")}
              okText={t("yes")}
              cancelText={t("no")}
            >
              <Button
                style={{ marginRight: "10px", float: "right", width: "100px" }}
              >
                {t("cancel")}
              </Button>
            </Popconfirm>
          </Form.Item>
        </Form>
      </Content>
    </div>
  );
};

export default CreateNews;
